import { configManager } from '../../../config/configManager'
import { SMARTLOOK_OMIT_URL_PATTERNS, SMARTLOOK_SENSITIVE_KEYS } from '../../../config/smartlook-config'

/**
 * Smartlook Initialization Utility
 *
 * Handles Smartlook initialization with advanced network recording and data masking.
 *
 * IMPORTANT (Smartlook project setting):
 * - DevTools must be enabled in Smartlook Project settings, otherwise advanced network recording will not work.
 *
 * Privacy-by-design notes:
 * - We only allow recording for our own API URLs (and same-origin /api/v1) to avoid capturing 3rd-party traffic.
 * - Auth, API-key, SMTP/email, and mailer calls are omitted entirely (interceptor returns false).
 * - Remaining bodies/headers are masked using Smartlook's nested `data.request` / `data.response` shape
 *   (see https://web.developer.smartlook.com/docs/network-interceptor).
 *
 * Docs: https://web.developer.smartlook.com/docs/advanced-network-recording
 */

const SMARTLOOK_PROJECT_KEY = 'db23ea8df5ac394d63fb3400977a108704d66b17'

type SmartlookHeaders = Record<string, unknown>

export type SmartlookNetworkData = {
  url?: string
  error?: string
  /** Official Smartlook shape */
  request?: {
    body?: string
    headers?: SmartlookHeaders
  }
  response?: {
    body?: string
    headers?: SmartlookHeaders
  }
  /** Legacy / defensive aliases (not used by current Smartlook web SDK) */
  headers?: SmartlookHeaders
  requestHeaders?: SmartlookHeaders
  responseHeaders?: SmartlookHeaders
  requestBody?: unknown
  responseBody?: unknown
  body?: unknown
}

/**
 * Check if Smartlook should be enabled for a given company ID
 */
export function isSmartlookEnabledForCompany(companyId: string | undefined): boolean {
  if (!companyId) return false

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname
    if (hostname === 'localhost' || hostname === '127.0.0.1') return false
  }

  const enabled = configManager.api.REACT_APP_SMARTLOOK_ENABLED === 'true'
  if (!enabled) return false

  const allowedCompanies = (configManager.api as Record<string, unknown>).REACT_APP_SMARTLOOK_SESSION_REPLAY_COMPANIES
  if (!allowedCompanies) return false

  // Allow all companies when set to '*' or 'all' (string or single-element array)
  const firstValue =
    typeof allowedCompanies === 'string'
      ? allowedCompanies.trim().toLowerCase()
      : Array.isArray(allowedCompanies) && allowedCompanies.length === 1
        ? String(allowedCompanies[0]).trim().toLowerCase()
        : ''
  if (firstValue === '*' || firstValue === 'all') return true

  // Handle both string (comma-separated) and array formats
  let companyList: string[]
  if (typeof allowedCompanies === 'string') {
    companyList = allowedCompanies.split(',').map((id: string) => id.trim())
  } else if (Array.isArray(allowedCompanies)) {
    companyList = allowedCompanies.map((id: unknown) => String(id).trim())
  } else {
    return false
  }

  return companyList.includes(companyId)
}

/**
 * Escape special regex characters in a string
 */
function escapeRegExp(str: string): string {
  return String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Get sensitive keys pattern for regex matching
 */
function getSensitiveKeysPattern(): string {
  return SMARTLOOK_SENSITIVE_KEYS.map((key) => escapeRegExp(key)).join('|')
}

/**
 * Mask sensitive query parameters in URLs
 */
export function maskUrl(url: string): string {
  if (!url || typeof url !== 'string') return url

  const keysPattern = getSensitiveKeysPattern()
  if (!keysPattern) return url

  const regex = new RegExp(`([?&#])(${keysPattern})=([^&#]*)`, 'gi')
  return url.replace(regex, '$1$2=[MASKED]')
}

/**
 * Mask sensitive data in object structures (recursive)
 */
export function maskObjectDeep(value: unknown): unknown {
  if (!value || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map(maskObjectDeep)

  const out: Record<string, unknown> = {}
  Object.keys(value as Record<string, unknown>).forEach((k) => {
    if (SMARTLOOK_SENSITIVE_KEYS.includes(k as (typeof SMARTLOOK_SENSITIVE_KEYS)[number])) {
      out[k] = '[MASKED]'
      return
    }
    out[k] = maskObjectDeep((value as Record<string, unknown>)[k])
  })
  return out
}

/**
 * Mask sensitive data in request/response bodies
 */
export function maskBody(body: unknown): unknown {
  if (!body) return body

  // Smartlook may provide request/response bodies as strings.
  if (typeof body === 'string') {
    // Try JSON first.
    try {
      const parsed = JSON.parse(body)
      return JSON.stringify(maskObjectDeep(parsed))
    } catch {
      // Not JSON; mask common URL-encoded patterns using sensitive keys.
      const keysPattern = getSensitiveKeysPattern()
      if (!keysPattern) return body

      const regex = new RegExp(`(\\b(${keysPattern})\\s*=\\s*)([^&\\s]+)`, 'gi')
      return body.replace(regex, '$1[MASKED]')
    }
  }

  // If bodies come as objects, mask them deeply.
  if (typeof body === 'object') return maskObjectDeep(body)

  return body
}

/**
 * Mask sensitive headers
 */
export function maskHeaders(headers: SmartlookHeaders): SmartlookHeaders {
  if (!headers || typeof headers !== 'object') return headers

  // Normalize common header names. We do NOT explicitly allow Authorization recording,
  // but if it is ever present in the captured data we mask it defensively.
  Object.keys(headers).forEach((k) => {
    const lower = String(k).toLowerCase()
    if (
      lower === 'authorization' ||
      lower === 'x-api-key' ||
      lower === 'x-company-token' ||
      lower === 'cookie' ||
      lower === 'set-cookie'
    ) {
      headers[k] = '[MASKED]'
    }
  })

  return headers
}

export function shouldOmitNetworkUrl(url: string | undefined): boolean {
  if (!url) return false
  const lower = url.toLowerCase()
  return SMARTLOOK_OMIT_URL_PATTERNS.some((pattern) => lower.includes(pattern.toLowerCase()))
}

/**
 * Apply Smartlook network interceptor mutations.
 * Returns false to drop the event entirely (sensitive endpoints).
 */
export function applySmartlookNetworkInterceptor(data: SmartlookNetworkData): false | void {
  if (!data) return

  if (shouldOmitNetworkUrl(data.url)) {
    return false
  }

  if (data.url) {
    data.url = maskUrl(data.url)
  }

  // Official Smartlook nested shape
  if (data.request?.headers) {
    data.request.headers = maskHeaders(data.request.headers)
  }
  if (data.response?.headers) {
    data.response.headers = maskHeaders(data.response.headers)
  }
  if (data.request && data.request.body !== undefined) {
    data.request.body = maskBody(data.request.body) as string | undefined
  }
  if (data.response && data.response.body !== undefined) {
    data.response.body = maskBody(data.response.body) as string | undefined
  }

  // Legacy aliases (defensive)
  if (data.headers) {
    data.headers = maskHeaders(data.headers)
  }
  if (data.requestHeaders) {
    data.requestHeaders = maskHeaders(data.requestHeaders)
  }
  if (data.responseHeaders) {
    data.responseHeaders = maskHeaders(data.responseHeaders)
  }
  if (data.requestBody !== undefined) {
    data.requestBody = maskBody(data.requestBody)
  }
  if (data.responseBody !== undefined) {
    data.responseBody = maskBody(data.responseBody)
  }
  if (data.body !== undefined) {
    data.body = maskBody(data.body)
  }
}

/**
 * Get allowed URLs for network recording
 */
function getAllowedUrls(): RegExp[] {
  const patterns: RegExp[] = [new RegExp(`^${escapeRegExp(window.location.origin)}/api/v1/`)]
  const gatewayUrl = configManager.api.GATEWAY_URL

  if (typeof gatewayUrl === 'string' && gatewayUrl.startsWith('http')) {
    const base = gatewayUrl.replace(/\/$/, '')
    const apiBase = base.includes('/api/v1') ? base : `${base}/api/v1`
    patterns.push(new RegExp(`^${escapeRegExp(apiBase)}/`))
  }

  return patterns
}

/**
 * Initialize Smartlook with advanced network recording and data masking
 */
export function initializeSmartlook(): void {
  if (!window.smartlook) {
    console.warn('Smartlook SDK not loaded. Call loadSmartlookSDK() first.')
    return
  }

  const allowedUrls = getAllowedUrls()

  window.smartlook('init', SMARTLOOK_PROJECT_KEY, {
    region: 'eu',
    advancedNetwork: {
      allowedUrls,
      // Enable WebSocket capture for debugging real-time flows (only for allowedUrls context).
      websockets: true,
    },
    interceptors: {
      network(data: SmartlookNetworkData) {
        return applySmartlookNetworkInterceptor(data)
      },
    },
  })
}
